import React from 'react';

import { cn } from '@/utils/classname';

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The icon element to render
   */
  icon: React.ReactNode;
  /**
   * Additional CSS classes to apply to the button
   */
  className?: string;
  /**
   * Accessible label for screen readers (required for accessibility)
   */
  'aria-label': string;
  /**
   * Size variant for the icon button
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Visual style variant
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
}

const sizeClasses = {
  sm: 'p-1',
  md: 'p-2',
  lg: 'p-3',
};

const variantClasses = {
  default:
    'text-chariot-text-medium hover:text-chariot-text-primary focus-visible:text-chariot-text-primary',
  primary:
    'text-chariot-primary-default hover:text-chariot-primary-dark focus-visible:text-chariot-primary-dark',
  danger:
    'text-chariot-stroke-critical hover:text-chariot-text-critical focus-visible:text-chariot-text-critical',
  ghost:
    'text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-background-secondary focus-visible:text-chariot-text-primary focus-visible:bg-chariot-background-secondary',
};

/**
 * IconButton - A reusable button component for clickable icons
 *
 * Provides consistent styling and behavior for icon-only buttons across the application:
 * - Automatic cursor pointer on hover
 * - Keyboard accessibility with focus-visible styles
 * - Multiple size and style variants
 * - Proper semantic HTML with required aria-label
 *
 * @example
 * ```tsx
 * <IconButton
 *   icon={<EyeIcon className="size-5" />}
 *   onClick={handleView}
 *   aria-label="View details"
 *   variant="primary"
 * />
 * ```
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      className,
      size = 'md',
      variant = 'default',
      type = 'button',
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'cursor-pointer transition-colors',
          'focus-visible:outline-chariot-brand-primary rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          // Size styles
          sizeClasses[size],
          // Variant styles
          variantClasses[variant],
          // Disabled styles
          disabled && 'cursor-not-allowed opacity-50',
          // Custom className
          className
        )}
        {...props}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
